import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { toast } from "sonner";
import {
  pushSupported,
  subscribePush,
  currentSubscription,
  isSubscribedLocally,
  dismissPrompt,
  promptDismissedRecently,
} from "@/lib/pushNotifications";

// Prompt discreto que aparece en Home invitando al miembro a activar
// notificaciones de cumpleaños. Se muestra solo si:
//  - Push soportado por el navegador
//  - No hay subscripción activa
//  - El user no descartó el prompt hace <30 días
//  - Miembro no es "invitado"

export default function PushNotificationsPrompt({ member, displayName }) {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!member || member === "invitado") return;
    if (!pushSupported()) return;
    if (isSubscribedLocally(member)) return;
    if (promptDismissedRecently(member)) return;
    // Chequea si ya hay subscripción real (por ejemplo migró de dispositivo)
    let cancelled = false;
    currentSubscription().then((sub) => {
      if (cancelled) return;
      if (!sub) setShow(true);
      else localStorage.setItem(`push_subscribed_${member}`, "1");
    });
    return () => { cancelled = true; };
  }, [member]);

  if (!show) return null;

  const handleActivate = async () => {
    setBusy(true);
    try {
      await subscribePush(member, displayName || member);
      toast.success("¡Listo! Te vamos a avisar de los cumples.");
      setShow(false);
    } catch (e) {
      const msg = String(e.message || e);
      if (msg.includes("denegado") || msg.includes("denied")) {
        toast.error("Permiso denegado. Actívalo desde Ajustes del navegador si te arrepientes.");
        dismissPrompt(member);
        setShow(false);
      } else {
        toast.error(`No se pudo activar: ${msg}`);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    dismissPrompt(member);
    setShow(false);
  };

  return (
    <div
      data-testid="push-prompt"
      className="mb-6 flex items-start gap-3 p-4 rounded-xl bg-cream border border-amber-deep/30 shadow-[0_10px_30px_-18px_rgba(58,26,5,0.3)]"
    >
      <div className="shrink-0 w-10 h-10 rounded-full bg-amber-deep/15 flex items-center justify-center">
        <Bell className="w-5 h-5 text-amber-deep" />
      </div>
      <div className="flex-1">
        <p className="font-display italic text-cocoa text-lg leading-tight mb-1">
          ¿Te aviso de los cumples?
        </p>
        <p className="font-serif text-cocoa-soft text-sm mb-3">
          Te llegará una notificación el día del cumple de cualquier miembro de la familia.
          Sin spam, solo cumpleaños.
        </p>
        <div className="flex gap-2">
          <button
            data-testid="push-prompt-activate"
            onClick={handleActivate}
            disabled={busy}
            className="btn-primary text-sm py-2"
          >
            {busy ? "Activando…" : "Activar"}
          </button>
          <button
            data-testid="push-prompt-dismiss"
            onClick={handleDismiss}
            className="btn-ghost text-sm py-2"
          >
            Ahora no
          </button>
        </div>
      </div>
      <button
        data-testid="push-prompt-close"
        onClick={handleDismiss}
        className="p-1 rounded-full hover:bg-line/50 transition"
        aria-label="Cerrar"
      >
        <X className="w-4 h-4 text-cocoa-soft" />
      </button>
    </div>
  );
}
